const METRIKA_ID = 109384227;
const CONSENT_KEY = 'pdfka:analytics-consent';
const CHANGE_EVENT = 'pdfka:analytics-consent-change';
const SCRIPT_ID = 'pdfka-yandex-metrika';

type AnalyticsConsent = 'accepted' | 'declined' | null;
type GoalParams = Record<string, string | number | boolean | null | undefined>;
type MetrikaFunction = ((counterId: number, method: string, ...args: unknown[]) => void) & {
  a?: unknown[][];
  l?: number;
};

declare global {
  interface Window {
    ym?: MetrikaFunction;
  }
}

let initialized = false;
let lastHitUrl = '';

export function isMetrikaHost(): boolean {
  const host = location.hostname.toLowerCase();
  return host === 'xn--80ahss0a.xn--p1ai' || host === 'www.xn--80ahss0a.xn--p1ai' || host === 'пдфка.рф' || host === 'www.пдфка.рф';
}

export function getAnalyticsConsent(): AnalyticsConsent {
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    return value === 'accepted' || value === 'declined' ? value : null;
  } catch {
    return null;
  }
}

export function setAnalyticsConsent(next: Exclude<AnalyticsConsent, null>): void {
  try {
    localStorage.setItem(CONSENT_KEY, next);
  } catch {
    // If localStorage is blocked, the banner can ask again on the next visit.
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }));
}

export function initMetrika(): void {
  if (initialized || !isMetrikaHost()) return;

  ensureMetrikaStub();
  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = `https://mc.yandex.ru/metrika/tag.js?id=${METRIKA_ID}`;
    document.head.appendChild(script);
  }

  initialized = true;
  lastHitUrl = currentUrl();
  callMetrika('init', {
    ssr: true,
    clickmap: true,
    referrer: document.referrer,
    url: location.href,
    accurateTrackBounce: true,
    trackLinks: true,
  });
}

export function trackPageView(): void {
  if (!canTrack()) return;
  const url = currentUrl();
  if (url === lastHitUrl) return;
  const referer = lastHitUrl || document.referrer;
  lastHitUrl = url;
  callMetrika('hit', url, {
    title: document.title,
    referer,
  });
}

export function trackGoal(name: string, params?: GoalParams): void {
  if (!canTrack()) return;
  callMetrika('reachGoal', name, sanitizeParams(params));
}

export function trackFileSelected(files: File[]): void {
  if (!files.length) return;
  const totalSize = files.reduce((sum, file) => sum + file.size, 0);
  const kinds = [...new Set(files.map(fileKind))];
  trackGoal('file_selected', {
    tool: currentTool(),
    files_count: Math.min(files.length, 50),
    file_kind: kinds.length === 1 ? kinds[0] : 'mixed',
    size_bucket: sizeBucket(totalSize),
  });
}

export function trackResultReady(mime: string, bytes?: number): void {
  trackGoal('result_ready', {
    tool: currentTool(),
    result_kind: mimeKind(mime),
    size_bucket: bytes ? sizeBucket(bytes) : undefined,
  });
}

export function trackDownload(mime?: string): void {
  trackGoal('download_click', {
    tool: currentTool(),
    result_kind: mime ? mimeKind(mime) : undefined,
  });
}

export function trackSharePromptView(): void {
  trackGoal('share_prompt_view', {
    tool: currentTool(),
  });
}

export function trackShareOpen(method: 'native'): void {
  trackGoal('share_open', {
    tool: currentTool(),
    method,
  });
}

export function trackToolError(error: unknown, scope = 'tool'): void {
  trackGoal('tool_error', {
    tool: currentTool(),
    scope,
    error_kind: errorKind(error),
  });
}

function canTrack(): boolean {
  if (!isMetrikaHost()) return false;
  initMetrika();
  return initialized && typeof window.ym === 'function';
}

function ensureMetrikaStub(): void {
  if (typeof window.ym === 'function') return;
  const stub = ((...args: unknown[]) => {
    stub.a = stub.a || [];
    stub.a.push(args);
  }) as MetrikaFunction;
  stub.l = Date.now();
  window.ym = stub;
}

function callMetrika(method: string, ...args: unknown[]): void {
  try {
    window.ym?.(METRIKA_ID, method, ...args);
  } catch {
    // Analytics must never break PDF tools.
  }
}

function currentUrl(): string {
  return `${location.origin}${location.pathname}${location.search}${location.hash}`;
}

function currentTool(): string {
  const hashTool = location.hash.replace(/^#\/?/, '').split('/')[0];
  if (hashTool) return normalizeParam(hashTool);
  const pathTool = location.pathname.replace(/^\/+|\/+$/g, '');
  return normalizeParam(pathTool || 'home');
}

function sanitizeParams(params?: GoalParams): Record<string, string | number | boolean> {
  const safe: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === null) continue;
    safe[normalizeParam(key)] = typeof value === 'string' ? normalizeParam(value) : value;
  }
  return safe;
}

function normalizeParam(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9а-яё_-]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80) || 'unknown';
}

function fileKind(file: File): string {
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  if (type.includes('wordprocessingml') || name.endsWith('.docx')) return 'docx';
  if (type.includes('spreadsheetml') || name.endsWith('.xlsx')) return 'xlsx';
  if (type.startsWith('image/') || /\.(jpe?g|png|webp)$/i.test(name)) return 'image';
  if (type.includes('csv') || name.endsWith('.csv')) return 'csv';
  if (type.includes('text') || name.endsWith('.txt')) return 'text';
  return 'other';
}

function mimeKind(mime: string): string {
  const value = mime.toLowerCase();
  if (value.includes('pdf')) return 'pdf';
  if (value.includes('zip')) return 'zip';
  if (value.includes('jpeg') || value.includes('jpg')) return 'jpg';
  if (value.includes('png')) return 'png';
  if (value.includes('word') || value.includes('docx')) return 'docx';
  if (value.includes('plain') || value.includes('text')) return 'text';
  return 'other';
}

function sizeBucket(bytes: number): string {
  const mb = bytes / 1024 / 1024;
  if (mb < 1) return '0_1mb';
  if (mb < 10) return '1_10mb';
  if (mb < 50) return '10_50mb';
  if (mb < 200) return '50_200mb';
  return '200mb_plus';
}

function errorKind(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  if (/parse|invalid pdf|pdf document/i.test(message)) return 'pdf_parse';
  if (/password|encrypt|protected/i.test(message)) return 'encrypted_pdf';
  if (/memory|allocation|quota/i.test(message)) return 'memory';
  if (/network|fetch|load/i.test(message)) return 'load';
  return 'other';
}
